/**
 * Pre-rendered aircraft marker bitmaps.
 *
 * Why this exists: each map marker used to be two sprite layers carrying CSS
 * filters — a 7-primitive colour chain plus four drop-shadow()s (a fake 0.5px
 * outline) on top, brightness/opacity/blur underneath for the ground shadow.
 * Every drop-shadow and blur is an offscreen surface and a convolution on the
 * compositor, re-done on every pan, zoom, heading change and setIcon(). With
 * ~100 aircraft that was ~360 drop-shadows per frame; the Live tab crashed in
 * Firefox first and then, on a flaky Intel GPU driver, in Chrome too.
 *
 * Here each (sprite cell, size, colour, outline) combination is rasterised ONCE
 * on a tiny canvas and handed to the marker as a data-URL <img>. Per-frame cost
 * becomes a composited transform and nothing else.
 */
import { useSyncExternalStore } from 'react';
import spriteImage from '../assets/BelugaProject/beluga_sprites.png';

export const SPRITE_CELL_PX = 72;   // beluga_sprites.png is 8 columns of 72px cells
export const MAX_CACHED_BITMAPS = 4000; // ~2 KB each; cleared wholesale when exceeded

const cache = new Map();
const listeners = new Set();
let sheet = null;
let sheetReady = false;

function loadSheet() {
  if (sheet || typeof Image === 'undefined') return;
  sheet = new Image();
  sheet.decoding = 'async';
  sheet.onload = () => {
    sheetReady = true;
    listeners.forEach((fn) => fn());
  };
  sheet.onerror = () => {
    console.error('[markerBitmaps] failed to load the aircraft sprite sheet');
  };
  sheet.src = spriteImage;
}

function subscribe(fn) {
  listeners.add(fn);
  loadSheet();
  return () => listeners.delete(fn);
}

const getSnapshot = () => sheetReady;

/** True once the sprite sheet has decoded; components re-render when it flips. */
export function useSpriteSheetReady() {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

export function isSpriteSheetReady() {
  return sheetReady;
}

function pixelRatio() {
  const dpr = typeof window !== 'undefined' && window.devicePixelRatio ? window.devicePixelRatio : 1;
  return Math.min(Math.max(dpr, 1), 2);
}

// A canvas per call was the wrong shape. getAircraftBitmap alone allocated three
// (destination + two silhouettes), and the cache key includes `size`, which moves with
// zoom — so a single zoom step missed for EVERY aircraft and put ~300 fresh canvases in
// flight at once. Two pooled canvases now serve every call for the life of the page.
const pool = { dest: null, scratch: null };

/**
 * A cleared, correctly sized canvas from `slot`.
 *
 * There are only two slots, so a caller may hold ONE 'dest' and ONE 'scratch' at a time.
 * Every silhouette() result MUST be consumed before the next silhouette() call — it is
 * literally the same canvas. Both callers do that today; keep it that way.
 */
function acquire(slot, size) {
  const dpr = pixelRatio();
  let entry = pool[slot];
  if (!entry) {
    const canvas = document.createElement('canvas');
    // willReadFrequently: every bitmap here ends in toDataURL(), i.e. a CPU readback.
    // Without the hint Firefox backs the canvas with a GPU surface and each readback is
    // a synchronous GPU->CPU stall. With ~100 aircraft re-rasterising on one zoom step
    // that repeatedly killed the GPU process — four identical null derefs at the same
    // instruction in xul.dll on 2026-09-25. These are read every time; keep them in
    // software, which is where a canvas you immediately serialise belongs.
    entry = pool[slot] = { canvas, ctx: canvas.getContext('2d', { willReadFrequently: true }) };
  }
  const { canvas, ctx } = entry;
  const px = Math.round(size * dpr);
  // Assigning width/height resets both the bitmap and the context state. The explicit
  // resets are belt and braces and document the contract: getShadowBitmap leaves
  // globalAlpha and filter set, and silhouette() leaves globalCompositeOperation set,
  // so a pooled context must never be handed on carrying them.
  canvas.width = px;
  canvas.height = px;
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, px, px);
  ctx.globalAlpha = 1;
  ctx.globalCompositeOperation = 'source-over';
  if ('filter' in ctx) ctx.filter = 'none';
  ctx.scale(dpr, dpr);
  return entry;
}

/** The sprite cell as a solid-colour silhouette (the sheet's alpha is the shape). */
function silhouette(col, row, size, colour) {
  const { canvas, ctx } = acquire('scratch', size);
  ctx.drawImage(sheet, col * SPRITE_CELL_PX, row * SPRITE_CELL_PX, SPRITE_CELL_PX, SPRITE_CELL_PX, 0, 0, size, size);
  ctx.globalCompositeOperation = 'source-in';
  ctx.fillStyle = colour;
  ctx.fillRect(0, 0, size, size);
  return canvas;
}

function remember(key, value) {
  if (cache.size >= MAX_CACHED_BITMAPS) cache.clear();
  cache.set(key, value);
  return value;
}

/**
 * Tinted sprite with a 0.5px outline — the same look the four drop-shadow()s
 * produced. Returns a data-URL, or null until the sheet is ready.
 */
export function getAircraftBitmap({ col, row, size, colour, outline = '#333' }) {
  if (!sheetReady) { loadSheet(); return null; }
  const key = `a|${col}|${row}|${size}|${colour}|${outline}|${pixelRatio()}`;
  const hit = cache.get(key);
  if (hit) return hit;

  const { canvas, ctx } = acquire('dest', size);
  const edge = silhouette(col, row, size, outline);
  for (const [dx, dy] of [[0.5, 0], [-0.5, 0], [0, 0.5], [0, -0.5]]) {
    ctx.drawImage(edge, dx, dy, size, size);
  }
  ctx.drawImage(silhouette(col, row, size, colour), 0, 0, size, size);
  return remember(key, canvas.toDataURL('image/png'));
}

/**
 * Soft ground shadow: black silhouette at 30% with a 1px blur, blurred once here
 * instead of by a CSS filter on every frame.
 */
export function getShadowBitmap({ col, row, size }) {
  if (!sheetReady) { loadSheet(); return null; }
  const key = `s|${col}|${row}|${size}|${pixelRatio()}`;
  const hit = cache.get(key);
  if (hit) return hit;

  const { canvas, ctx } = acquire('dest', size);
  ctx.globalAlpha = 0.3;
  if ('filter' in ctx) ctx.filter = 'blur(1px)';
  ctx.drawImage(silhouette(col, row, size, '#000'), 0, 0, size, size);
  return remember(key, canvas.toDataURL('image/png'));
}

/** Test/diagnostic hooks. */
export function _bitmapCacheSize() {
  return cache.size;
}
export function _resetBitmapCache() {
  cache.clear();
}
export function _setSheetForTests(image, ready = true) {
  sheet = image;
  sheetReady = ready;
  listeners.forEach((fn) => fn());
}
